import { PageLayout } from '../../layout/PageLayout'
import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { Banner } from '@app/components/core/banner/Banner'
import { useState } from 'react'
import useMediaQuery from '@app/util/useMediaQuery'
import { useQuery } from '@based/sdk/react'
import { Avatar } from '@app/components/core/avatar/Avatar'
import { PageHeader } from '@app/components/blocks/pageHeader/PageHeader'
import { RightColumn } from './sections/RightColumn'
import Row from '@app/components/layout/Row'
import { useOpenEmailCompose } from '@app/util/hooks/useOpenEmailCompose'
import { SocialShareModal } from '@app/components/blocks/modal/SocialShareModal'
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons'
import { TabContent } from './profileTabs/TabContent'
import { BusinessProfile } from '@schema/businessProfile'
import { Title } from '@app/components/core/typography/Title'

export type RegionBusinessProfilePageProps = {
  businessProfileId: string
}

export const RegionBusinessProfilePage = ({
  businessProfileId,
}: RegionBusinessProfilePageProps) => {
  const isMobile = useMediaQuery()
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const openEmailCompose = useOpenEmailCompose()
  const t = useTranslations().app

  const query = useQuery('view-business-profile', { slug: businessProfileId })
  const businessProfile: BusinessProfile = query.data

  if (!businessProfile) {
    return (
      <PageLayout gap="2rem">
        <Column
          gap="1.5rem"
          alignItems="stretch"
          style={{ marginBottom: '25rem' }}
        >
          <Title.H2>{t.common.loading}</Title.H2>
        </Column>
      </PageLayout>
    )
  }

  if (isMobile) {
    return (
      <>
        <PageLayout gap="2rem">
          <Column gap="1.5rem" alignItems="stretch">
            <Banner image={businessProfile.banner?.src}>
              <Avatar
                image={businessProfile.avatar}
                size="medium"
                shape="square"
              />
            </Banner>

            <PageHeader
              title={businessProfile.title}
              subtitle={businessProfile.intro}
              tags={businessProfile.type?.map((type) => ({
                label: type.name,
                variant: 'blueMuted',
                size: 'small',
              }))}
              hasBadge={businessProfile.crowdbuildingPartner}
              buttons={[
                {
                  label: t.common.share,
                  onClick: () => setIsShareModalOpen(true),
                  variant: 'secondary',
                  size: 'small',
                },
                {
                  label: t.common.contact,
                  onClick: () =>
                    openEmailCompose(businessProfile.contact?.email),
                  variant: 'primary',
                  icon: faEnvelope,
                  iconPosition: 'left',
                  size: 'small',
                },
              ]}
            />
            <RightColumn businessProfile={businessProfile} />
            <TabContent businessProfile={businessProfile} />
          </Column>
        </PageLayout>

        <SocialShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          title={businessProfile.title}
        />
      </>
    )
  }

  return (
    <>
      <PageLayout gap="2rem">
        <Column gap="1.5rem" alignItems="stretch">
          <Banner image={businessProfile.banner?.src}>
            <Avatar
              image={businessProfile.avatar}
              size="large"
              shape="square"
            />
          </Banner>

          <PageHeader
            title={businessProfile.title}
            subtitle={businessProfile.intro}
            tags={businessProfile.type?.map((type) => ({
              label: type.name,
              variant: 'blueMuted',
              size: 'small',
            }))}
            hasBadge={businessProfile.crowdbuildingPartner}
            buttons={[
              {
                label: t.common.share,
                onClick: () => setIsShareModalOpen(true),
                variant: 'secondary',
              },
              {
                label: t.common.contact,
                onClick: () => openEmailCompose(businessProfile.contact?.email),
                variant: 'primary',
                icon: faEnvelope,
                iconPosition: 'left',
              },
            ]}
          />

          <Row gap="1.5rem" style={{ alignItems: 'flex-start' }}>
            <Column
              alignItems="stretch"
              gap="3.5rem"
              style={{ flex: '2 1 auto' }}
            >
              <TabContent businessProfile={businessProfile} />
            </Column>
            <RightColumn businessProfile={businessProfile} />
          </Row>
        </Column>
      </PageLayout>

      <SocialShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title={businessProfile.title}
      />
    </>
  )
}
